import {
  Image,
  Pressable,
  type StyleProp,
  View,
  type ViewStyle,
} from 'react-native'
import {
  AppBskyEmbedImages,
  type AppBskyFeedDefs,
  AppBskyFeedPost,
} from '@atproto/api'
import {msg} from '@lingui/core/macro'
import {useLingui} from '@lingui/react'

import {useGetTimeAgo} from '#/lib/hooks/useTimeAgo'
import {sanitizeHandle} from '#/lib/strings/handles'
import {formatCount} from '#/view/com/util/numeric/format'
import {UserAvatar} from '#/view/com/util/UserAvatar'
import {atoms as a, useTheme} from '#/alf'
import {Bubble_Stroke2_Corner2_Rounded as BubbleIcon} from '#/components/icons/Bubble'
import {DotGrid3x1_Stroke2_Corner0_Rounded as EllipsisIcon} from '#/components/icons/DotGrid'
import {Heart2_Stroke2_Corner0_Rounded as HeartIcon} from '#/components/icons/Heart2'
import {Repost_Stroke2_Corner2_Rounded as RepostIcon} from '#/components/icons/Repost'
import {Text} from '#/components/Typography'
import {useSemanticTokens} from '#/wsocial/service/design-system'
import {usePostThreadPress} from '../util'

/**
 * Full post preview shown inside hero trend cards.
 *
 * Mirrors Figma node `384:53798` in file `C8J3bA4ThNVCG0vzlHeI16`: author row
 * (42px avatar · name · handle · time), full post text, an optional image
 * embed, and a display-only reply/repost/like count bar with a trailing
 * ellipsis. The counts are intentionally **not** interactive — tapping
 * anywhere on the card opens the post thread, where the full controls live.
 *
 * Featured cards keep using the lighter `CompactPostPreview`; this fuller
 * variant is reserved for `section.cardStyle === 'hero'`.
 *
 * Embed support is images-only for now (first image of the embed). Link
 * cards, quotes, and video are deliberately omitted from v1 of the teaser.
 */
export function FullPostPreview({
  post,
  style,
}: {
  post: AppBskyFeedDefs.PostView
  style?: StyleProp<ViewStyle>
}) {
  const t = useTheme()
  const ss = useSemanticTokens()
  const {_} = useLingui()
  const getTimeAgo = useGetTimeAgo()
  const onPostPress = usePostThreadPress()

  const author = post.author
  const record = post.record
  const text =
    AppBskyFeedPost.isRecord(record) && typeof record.text === 'string'
      ? record.text
      : ''
  const name = author.displayName?.trim() || sanitizeHandle(author.handle)
  const timeAgo = getTimeAgo(post.indexedAt, Date.now())

  const image = AppBskyEmbedImages.isView(post.embed)
    ? post.embed.images[0]
    : undefined

  return (
    <Pressable
      accessibilityRole="link"
      accessibilityLabel={_(msg`Post by ${name}`)}
      accessibilityHint={_(msg`Opens the post`)}
      onPress={() => onPostPress(post)}
      style={[
        a.w_full,
        a.flex_row,
        a.gap_md,
        {padding: 20, borderRadius: 24, backgroundColor: ss.bg.contrast_25},
        style,
      ]}>
      <UserAvatar type="user" size={42} avatar={author.avatar} />

      <View style={[a.flex_1, {gap: 6}]}>
        {/* Header: name · handle · time */}
        <View style={[a.flex_row, a.align_center, a.gap_xs]}>
          <Text numberOfLines={1} style={[a.flex_1, a.text_md, a.leading_snug]}>
            <Text style={[a.font_bold, t.atoms.text]}>{name}</Text>
            <Text style={[t.atoms.text_contrast_medium]}>
              {'  '}
              {sanitizeHandle(author.handle)} · {timeAgo}
            </Text>
          </Text>
        </View>

        <Text style={[a.text_md, a.leading_snug, t.atoms.text]}>{text}</Text>

        {image && (
          <Image
            accessibilityIgnoresInvertColors
            accessibilityLabel={image.alt}
            accessibilityHint=""
            accessible={!!image.alt}
            source={{uri: image.thumb}}
            style={[
              a.w_full,
              a.rounded_md,
              a.my_md,
              {
                aspectRatio: image.aspectRatio
                  ? image.aspectRatio.width / image.aspectRatio.height
                  : 16 / 9,
                backgroundColor: ss.bg.contrast_50,
              },
            ]}
            resizeMode="cover"
          />
        )}

        {/* Display-only interaction counts + ellipsis. Mirrors the layout of
            regular posts (`PostControls`): each stat is a flex_1 column inside
            a maxWidth row, first icon pulled flush to the text edge, and the
            menu affordance pinned to the right. */}
        <View
          style={[a.flex_row, a.align_center, a.justify_between, a.gap_md]}
          pointerEvents="none">
          <View style={[a.flex_row, a.flex_1, {maxWidth: 220}]}>
            <View style={[a.flex_1, a.align_start, {marginLeft: -5}]}>
              <StatItem icon={BubbleIcon} count={post.replyCount ?? 0} />
            </View>
            <View style={[a.flex_1, a.align_start]}>
              <StatItem icon={RepostIcon} count={post.repostCount ?? 0} />
            </View>
            <View style={[a.flex_1, a.align_start]}>
              <StatItem icon={HeartIcon} count={post.likeCount ?? 0} />
            </View>
            {/* Spacer — keeps the three stats from spreading edge-to-edge,
                matching `PostControls`. */}
            <View />
          </View>
          <EllipsisIcon width={18} fill={t.atoms.text_contrast_medium.color} />
        </View>
      </View>
    </Pressable>
  )
}

function StatItem({
  icon: Icon,
  count,
}: {
  icon: typeof BubbleIcon
  count: number
}) {
  const t = useTheme()
  const {i18n} = useLingui()
  return (
    // Figma "Interaction Bar/Parts": 5px pill padding, 6px icon↔count gap,
    // count is 13px on a 16px line box so digits center against the 18px icon.
    <View style={[a.flex_row, a.align_center, {gap: 6, padding: 5}]}>
      <Icon width={18} fill={t.atoms.text_contrast_medium.color} />
      {count > 0 && (
        <Text
          style={[a.text_sm, {lineHeight: 16}, t.atoms.text_contrast_medium]}>
          {formatCount(i18n, count)}
        </Text>
      )}
    </View>
  )
}
